import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Stack,
  Chip,
} from '@mui/material';
import {
  Landscape as LandscapeIcon,
  TrendingUp as TrendingUpIcon,
  HandshakeOutlined as HandshakeIcon,
  PublicOutlined as PublicIcon,
  ForumOutlined as ForumIcon,
  TuneOutlined as TuneIcon,
  ArrowForward as ArrowForwardIcon,
  PlaceOutlined as PlaceIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import LogoReveal from '../components/LogoReveal';
import ScrollVideoBackground from '../components/ScrollVideoBackground';

// Brand system (dark + gold) now lives in the shared theme — single source of truth.
import { C, DISPLAY, reduceMotion } from '../theme';

const Home: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const countries = ['Srbija', 'Hrvatska', 'Bosna i Hercegovina', 'Crna Gora', 'Makedonija'];

  return (
    <>
      <SEO
        title="Početna"
        description="Platforma za povezivanje vlasnika zemljišta, vlasnika biznisa i investitora. Pronađite investicijske prilike ili stupite u kontakt sa investitorima."
        keywords="investitor, zemljiste, nekretnine, investicija, biznis, poslovne prilike, gradjevina, poslovni prostor, oglas, restoran, trgovina, proizvodnja"
        url="/"
      />

      <Box sx={{ pt: '70px', backgroundColor: C.bg }}>
        {/* ============================ HERO ============================ */}
        <Box
          component="section"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            color: C.white,
            minHeight: { xs: 'auto', md: '88vh' },
            display: 'flex',
            alignItems: 'center',
            py: { xs: 9, md: 0 },
            backgroundColor: C.bg,
          }}
        >
          <ScrollVideoBackground
            src="/interior-bg.mp4"
            poster="/interior-poster.jpg"
            framePrefix="/frames/interior/"
            frameCount={100}
            overlay="linear-gradient(100deg, rgba(7,7,8,0.95) 0%, rgba(7,7,8,0.82) 44%, rgba(7,7,8,0.50) 100%)"
          />
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ maxWidth: 720 }}>
              <Chip
                label="Balkanska platforma za investicije"
                sx={{
                  mb: 3,
                  color: '#F6CB4B',
                  backgroundColor: C.goldSoft,
                  border: '1px solid rgba(243,192,21,0.4)',
                  fontWeight: 500,
                  letterSpacing: '0.3px',
                }}
              />

              <Typography
                component="h1"
                sx={{
                  fontFamily: DISPLAY,
                  fontWeight: 800,
                  fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4.4rem' },
                  lineHeight: 1.04,
                  letterSpacing: '-0.5px',
                  mb: 3,
                }}
              >
                Pronađite investitora za svoje{' '}
                <Box
                  component="span"
                  sx={{
                    fontStyle: 'italic',
                    background: C.goldGrad,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    display: 'inline-block',
                    pr: '0.12em', // room for the italic overhang so the last glyph isn't clipped
                  }}
                >
                  zemljište
                </Box>{' '}
                ili biznis.
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.25rem' },
                  lineHeight: 1.6,
                  color: 'rgba(255,255,255,0.82)',
                  maxWidth: 560,
                  mb: 4,
                }}
              >
                Direktno povezujemo vlasnike zemljišta i biznisa sa investitorima širom
                regiona — bez agencija, bez posrednika, bez provizije.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                <Button
                  size="large"
                  onClick={() => navigate(isAuthenticated ? '/create-ad' : '/register')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: C.goldGrad,
                    color: C.onGold,
                    px: 4,
                    py: 1.6,
                    fontSize: '1.02rem',
                    fontWeight: 700,
                    boxShadow: '0 8px 26px rgba(224,162,31,0.35)',
                    transition: 'box-shadow .2s ease, filter .2s ease',
                    '&:hover': {
                      filter: 'brightness(1.06)',
                      transform: 'none',
                      boxShadow: '0 10px 32px rgba(224,162,31,0.5)',
                    },
                    [reduceMotion]: { transition: 'none' },
                  }}
                >
                  Postavi oglas
                </Button>
                <Button
                  size="large"
                  onClick={() => navigate('/nekretnine')}
                  sx={{
                    backgroundColor: 'transparent',
                    color: '#F6CB4B',
                    border: '1.5px solid rgba(243,192,21,0.55)',
                    px: 4,
                    py: 1.6,
                    fontSize: '1.02rem',
                    fontWeight: 600,
                    transition: 'background-color .2s ease, border-color .2s ease',
                    '&:hover': {
                      backgroundColor: C.goldSoft,
                      borderColor: C.gold,
                      transform: 'none',
                    },
                    [reduceMotion]: { transition: 'none' },
                  }}
                >
                  Pretraži prilike
                </Button>
              </Stack>

              <Stack
                direction="row"
                spacing={1.2}
                useFlexGap
                flexWrap="wrap"
                sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.92rem' }}
              >
                {['Zemljište', 'Nekretnine', 'Biznisi'].map((t) => (
                  <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.7, pr: 1.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: C.gold }} />
                    {t}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* ===================== REGIONAL COVERAGE STRIP ===================== */}
        <Box sx={{ backgroundColor: C.surface, color: C.white, py: 2.5, borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1.5, md: 0 }}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PublicIcon sx={{ fontSize: 20, color: C.gold }} />
                <Typography sx={{ fontWeight: 600, letterSpacing: '0.3px' }}>
                  Dostupno širom regiona
                </Typography>
              </Box>
              <Stack direction="row" spacing={{ xs: 1, md: 2.5 }} useFlexGap flexWrap="wrap" justifyContent="center">
                {countries.map((c) => (
                  <Typography key={c} sx={{ fontSize: '0.92rem', color: C.muted }}>
                    {c}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* ===================== TWO AUDIENCES ===================== */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ maxWidth: 620, mb: { xs: 5, md: 7 } }}>
            <SectionEyebrow>ZA KOGA JE PLATFORMA</SectionEyebrow>
            <Typography
              component="h2"
              sx={{ fontFamily: DISPLAY, fontWeight: 700, color: C.white, fontSize: { xs: '2rem', md: '2.75rem' }, lineHeight: 1.1, mt: 1.5 }}
            >
              Dve strane, jedan dogovor
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Owners */}
            <Grid item xs={12} md={6}>
              <AudiencePanel
                image="/images/home.jpg"
                icon={<LandscapeIcon sx={{ fontSize: 26 }} />}
                tag="Vlasnici"
                title="Imate zemljište ili biznis?"
                bullets={[
                  'Postavite oglas besplatno, za nekoliko minuta',
                  'Dođite do investitora iz celog regiona',
                  'Pregovarajte direktno, bez agencija i provizije',
                ]}
                ctaLabel="Postavi oglas"
                onCta={() => navigate(isAuthenticated ? '/create-ad' : '/register')}
              />
            </Grid>
            {/* Investors */}
            <Grid item xs={12} md={6}>
              <AudiencePanel
                image="/images/office.jpg"
                icon={<TrendingUpIcon sx={{ fontSize: 26 }} />}
                tag="Investitori"
                title="Tražite priliku?"
                bullets={[
                  'Pretražite zemljište, nekretnine i biznise na jednom mestu',
                  'Filtrirajte po državi, ceni, površini i tipu',
                  'Kontaktirajte vlasnika direktno preko platforme',
                ]}
                ctaLabel="Pretraži prilike"
                onCta={() => navigate('/business')}
              />
            </Grid>
          </Grid>
        </Container>

        {/* ===================== HOW IT WORKS ===================== */}
        <Box sx={{ backgroundColor: C.surface, py: { xs: 8, md: 12 }, borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 5, md: 8 } }}>
              <SectionEyebrow center>KAKO FUNKCIONIŠE</SectionEyebrow>
              <Typography
                component="h2"
                sx={{ fontFamily: DISPLAY, fontWeight: 700, color: C.white, fontSize: { xs: '2rem', md: '2.75rem' }, lineHeight: 1.1, mt: 1.5 }}
              >
                Od oglasa do dogovora u tri koraka
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {[
                { n: '01', t: 'Registrujte se', d: 'Otvorite nalog kao vlasnik ili kao investitor — besplatno i bez obaveza.' },
                { n: '02', t: 'Postavite ili pretražite', d: 'Vlasnici postavljaju oglas, investitori pretražuju i filtriraju prilike po regionu.' },
                { n: '03', t: 'Povežite se direktno', d: 'Dogovarajte detalje kroz ugrađene poruke — bez posrednika između vas.' },
              ].map((s) => (
                <Grid item xs={12} md={4} key={s.n}>
                  <Box sx={{ pt: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: DISPLAY,
                        fontSize: '3rem',
                        fontWeight: 800,
                        lineHeight: 1,
                        mb: 1.5,
                        background: C.goldGrad,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        width: 'fit-content',
                      }}
                    >
                      {s.n}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: C.white, mb: 1 }}>
                      {s.t}
                    </Typography>
                    <Typography sx={{ color: C.muted, lineHeight: 1.65 }}>{s.d}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ===================== WHY US ===================== */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ maxWidth: 620, mb: { xs: 5, md: 7 } }}>
            <SectionEyebrow>ZAŠTO NAĐI INVESTITORA</SectionEyebrow>
            <Typography
              component="h2"
              sx={{ fontFamily: DISPLAY, fontWeight: 700, color: C.white, fontSize: { xs: '2rem', md: '2.75rem' }, lineHeight: 1.1, mt: 1.5 }}
            >
              Napravljeno za naše tržište
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 4, md: 5 }}>
            {[
              { icon: <PublicIcon />, t: 'Fokus na region', d: 'Lokalni gradovi i lokacije širom Srbije, Hrvatske, BiH, Crne Gore i Makedonije — i publika koja razume tržište.' },
              { icon: <HandshakeIcon />, t: 'Bez posrednika', d: 'Povežite se direktno sa donosiocem odluke. Bez agencija i bez provizije između strana.' },
              { icon: <ForumIcon />, t: 'Direktne poruke', d: 'Pregovarajte i dogovarajte obilaske unutar platforme, bez otkrivanja ličnih kontakata.' },
              { icon: <TuneIcon />, t: 'Napredni filteri', d: 'Pronađite tačno ono što tražite — po državi, ceni, površini i tipu — u nekoliko klikova.' },
            ].map((f) => (
              <Grid item xs={12} sm={6} key={f.t}>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: C.goldSoft,
                      color: C.gold,
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: C.white, mb: 0.8 }}>
                      {f.t}
                    </Typography>
                    <Typography sx={{ color: C.muted, lineHeight: 1.65 }}>{f.d}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* ===================== LOGO REVEAL (scroll-drawn crest) ===================== */}
        <LogoReveal />

        {/* CTA uklonjen — samo crna pozadina ispod logoa */}
      </Box>

      {/* ============================ FOOTER ============================ */}
      <Box component="footer" sx={{ backgroundColor: '#070708', color: C.muted, py: 6, borderTop: `1px solid ${C.line}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <img src="/logo.png" alt="Nađi Investitora" style={{ height: 40, width: 'auto' }} />
                <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.35rem', fontWeight: 700, color: C.white }}>
                  Nađi Investitora
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.95rem', maxWidth: 420, lineHeight: 1.6 }}>
                Platforma koja direktno povezuje vlasnike zemljišta i biznisa sa investitorima širom Balkana.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                direction="row"
                spacing={3}
                useFlexGap
                flexWrap="wrap"
                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              >
                <FooterLink href="/contact">Kontakt</FooterLink>
                <FooterLink href="/privacy">Politika privatnosti</FooterLink>
                <FooterLink href="/uploads/docs/TC.pdf" external>Uslovi korišćenja</FooterLink>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: `1px solid ${C.line}`, mt: 4, pt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlaceIcon sx={{ fontSize: 16, color: C.gold }} />
            <Typography sx={{ fontSize: '0.85rem' }}>
              © {new Date().getFullYear()} Nađi Investitora. Sva prava zadržana.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

// ---------- small presentational helpers (kept in-file for one self-contained page) ----------

const SectionEyebrow: React.FC<{ children: React.ReactNode; center?: boolean }> = ({ children, center }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: center ? 'center' : 'flex-start' }}>
    <Box sx={{ width: 24, height: 2, background: C.goldGrad }} />
    <Typography sx={{ color: C.gold, fontWeight: 700, fontSize: '0.78rem', letterSpacing: '1.5px' }}>
      {children}
    </Typography>
  </Box>
);

interface AudiencePanelProps {
  image: string;
  icon: React.ReactNode;
  tag: string;
  title: string;
  bullets: string[];
  ctaLabel: string;
  onCta: () => void;
}

const AudiencePanel: React.FC<AudiencePanelProps> = ({ image, icon, tag, title, bullets, ctaLabel, onCta }) => (
  <Box
    sx={{
      height: '100%',
      borderRadius: 3,
      overflow: 'hidden',
      border: `1px solid ${C.line}`,
      backgroundColor: C.surface,
      display: 'flex',
      flexDirection: 'column',
      transition: 'border-color .25s ease, box-shadow .25s ease',
      '&:hover': { borderColor: 'rgba(243,192,21,0.5)', boxShadow: '0 18px 44px rgba(0,0,0,0.5)' },
      [reduceMotion]: { transition: 'none' },
    }}
  >
    <Box sx={{ position: 'relative', height: 200 }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(180deg, rgba(10,10,11,0.25) 0%, rgba(10,10,11,0.85) 100%), url('${image}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Chip
        icon={<Box sx={{ display: 'flex', color: 'inherit !important' }}>{icon}</Box>}
        label={tag}
        sx={{
          position: 'absolute',
          left: 20,
          bottom: 16,
          color: C.onGold,
          background: C.goldGrad,
          fontWeight: 700,
          '& .MuiChip-icon': { color: C.onGold },
        }}
      />
    </Box>
    <Box sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      <Typography sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: '1.6rem', color: C.white, mb: 2.5 }}>
        {title}
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 3.5, flexGrow: 1 }}>
        {bullets.map((b) => (
          <Box key={b} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{ flexShrink: 0, mt: '7px', width: 7, height: 7, borderRadius: '50%', backgroundColor: C.gold }} />
            <Typography sx={{ color: C.muted, lineHeight: 1.55 }}>{b}</Typography>
          </Box>
        ))}
      </Stack>
      <Button
        onClick={onCta}
        endIcon={<ArrowForwardIcon />}
        sx={{
          alignSelf: 'flex-start',
          background: C.goldGrad,
          color: C.onGold,
          px: 3,
          py: 1.2,
          fontWeight: 700,
          '&:hover': { filter: 'brightness(1.06)', transform: 'none' },
          [reduceMotion]: { transition: 'none' },
        }}
      >
        {ctaLabel}
      </Button>
    </Box>
  </Box>
);

const FooterLink: React.FC<{ href: string; external?: boolean; children: React.ReactNode }> = ({ href, external, children }) => (
  <Button
    href={href}
    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    disableRipple
    sx={{
      color: C.muted,
      p: 0,
      minWidth: 0,
      fontWeight: 500,
      backgroundColor: 'transparent',
      '&:hover': { color: C.gold, backgroundColor: 'transparent', transform: 'none' },
    }}
  >
    {children}
  </Button>
);

export default Home;
